import TagIds from '~/constants/common/tagIds';
import { useGetShowEVTaxTreatment } from '~/hooks/vdp';
import { getVehicleDetails } from '~/selectors';
import useSelector from '~/store/useSelector';
import { isDefined } from '~/utilities/array';
import { FeatureTourHighlightCards } from './constants';
import CarfaxLink from '../CarfaxLink';

type CallToActionProps =
  | React.HTMLProps<HTMLAnchorElement>
  | React.HTMLProps<HTMLButtonElement>;

export type FeatureTourHighlightInfo = {
  cardType: FeatureTourHighlightCards;
  title: string;
  subtitle?: string;
  imgProps?: React.ImgHTMLAttributes<HTMLImageElement>;
  listItems?: string[];
  callToAction?: (props: CallToActionProps) => React.JSX.Element;
  condition: boolean;
};

const cdnUrl = process.env.CVNA_APP_CDN_ASSETS_PATH;

export default function useGetFeatureTourHighlights(): FeatureTourHighlightInfo[] {
  const {
    highlights = [],
    remainingWarrantyMonths = 0,
    remainingWarrantyMiles = 0,
    remainingDriveTrainWarrantyMiles = 0,
    remainingDriveTrainWarrantyMonths = 0,
    vehicleId
  } = useSelector(getVehicleDetails);
  const showEVTaxTreatment = useGetShowEVTaxTreatment();
  const showNoAccidents = highlights?.some(
    highlight => highlight.tagId === TagIds.NoReportedAccidents
  );
  const hasOriginalWarranty =
    remainingWarrantyMonths > 1 && remainingWarrantyMiles > 0;
  const hasPowerTrainWarranty =
    remainingDriveTrainWarrantyMonths > 0 &&
    remainingDriveTrainWarrantyMiles > 1;
  const showSingleOwner = highlights?.some(
    highlight => highlight.tagId === TagIds.SingleOwner
  );
  const hasLowMileageTag = highlights?.some(
    highlight => highlight.tagId === TagIds.LowMileage
  );
  const hasLowMilesPerYearTag = highlights?.some(
    highlight => highlight.tagId === TagIds.LowMilesPerYear
  );

  return [
    {
      cardType: FeatureTourHighlightCards.NoAccident,
      title: 'No Reported Accidents',
      imgProps: {
        src: `${cdnUrl}/svgs/carfax.svg`,
        alt: 'Carfax Logo'
      },
      callToAction: props => {
        return (
          <CarfaxLink
            {...props}
            data-analytics-id="Highlights - View CARFAX Report"
            className="tl-label-m"
          >
            View Report
          </CarfaxLink>
        );
      },
      condition: showNoAccidents
    },
    {
      cardType: FeatureTourHighlightCards.Warranty,
      title: 'Original Warranty Still Active',
      listItems: [
        hasOriginalWarranty ? 'Basic warranty' : null,
        hasPowerTrainWarranty ? 'Powertrain warranty' : null
      ].filter(isDefined),
      condition: hasOriginalWarranty || hasPowerTrainWarranty
    },
    {
      cardType: FeatureTourHighlightCards.SingleOwner,
      title: 'Single Owner',
      subtitle: 'History reports show only one prior owner',
      callToAction: () => (
        <a
          className="mt-auto w-fit tl-label-m"
          target="_blank"
          rel="noopener noreferrer"
          href={`/vehicle/autocheck/${vehicleId}`}
          data-analytics-id="Highlights - View AutoCheck Report"
        >
          Details
        </a>
      ),
      condition: showSingleOwner
    },
    {
      cardType: FeatureTourHighlightCards.LowMileage,
      title: hasLowMileageTag ? 'Low Mileage' : 'Low Annual Mileage',
      listItems: [
        hasLowMilesPerYearTag
          ? 'Less than 9,000 miles per year on average'
          : null,
        hasLowMileageTag ? 'Less than 20,000 miles' : null
      ].filter(isDefined),
      condition: hasLowMileageTag || hasLowMilesPerYearTag
    },
    {
      cardType: FeatureTourHighlightCards.EvTaxCredit,
      title: 'EV Tax Credit Eligible',
      listItems: ['Qualifies for Federal Used EV Tax Credit'],
      callToAction: () => (
        <a
          className="mt-auto w-fit tl-label-m"
          target="_blank"
          rel="noopener noreferrer"
          href="/used-ev-tax-credit"
          data-analytics-id="Highlights - View EV Page"
        >
          Learn More
        </a>
      ),
      condition: showEVTaxTreatment
    }
  ].filter(item => item.condition);
}
